import { useRef } from 'react';
import PropTypes from 'prop-types';
import {
  AlertCircle,
  CheckCircle2,
  FileText,
  Flag,
  MinusCircle,
  XCircle,
} from 'lucide-react';
import ScopeFailedAnalysesTab from './ScopeFailedAnalysesTab';
import ScopeErrorBanner from './ScopeErrorBanner';
import ScopeAnalysisLoading from './ScopeAnalysisLoading';
import {
  NoSpecificationsEmpty,
  NoSubcontractorResults,
} from './ScopeEmptyStates';
import './scope-works.css';

/**
 * Scope of Works results view — redesign (AI2-546).
 *
 * Ported from the Claude Design prototype (QSAI Redesign v2): a single
 * scrolling page replacing the previous tabbed layout —
 *
 *   1. intro card describing the analysed scope document
 *   2. per-subcontractor coverage summary cards (alignment + counts)
 *   3. coverage matrix of every extracted spec vs every subcontractor
 *   4. additional items priced beyond the scope
 *   5. potential issues cards
 *   6. failed analyses (only when a subcontractor's analysis errored)
 */

const COVERAGE = {
  covered: { tone: 'success', label: 'Covered', Icon: CheckCircle2 },
  'partially covered': { tone: 'warning', label: 'Partial', Icon: MinusCircle },
  'not covered': { tone: 'danger', label: 'Not covered', Icon: XCircle },
};

const ALIGNMENT_TONE = { high: 'success', medium: 'warning', low: 'danger' };

const CoverageChip = ({ coverage }) => {
  const config = COVERAGE[coverage] || COVERAGE['not covered'];
  const { Icon } = config;
  return (
    <span className={`sw-chip tone-${config.tone}`}>
      <Icon size={13} />
      {config.label}
    </span>
  );
};

CoverageChip.propTypes = {
  coverage: PropTypes.string,
};

const SummaryCard = ({ name, result }) => {
  const items = Array.isArray(result?.items_covered)
    ? result.items_covered
    : [];
  const counts = items.reduce(
    (acc, item) => {
      const coverage = item.coverage || 'not covered';
      if (coverage === 'covered') acc.covered += 1;
      else if (coverage === 'partially covered') acc.partial += 1;
      else acc.notCovered += 1;
      return acc;
    },
    { covered: 0, partial: 0, notCovered: 0 }
  );
  const score = String(result?.overall_alignment?.score || '').toLowerCase();

  return (
    <div className="sw-card">
      <div className="sw-count-head">
        <span className="sw-chip sw-sub-chip tone-neutral">{name}</span>
        <span className="spacer" />
        <span className={`sw-chip tone-${ALIGNMENT_TONE[score] || 'warning'}`}>
          {score ? `${score} alignment` : 'alignment n/a'}
        </span>
      </div>
      <div className="sw-counts">
        <div className="sw-count">
          <span className="sw-count-dot covered" />
          <span>
            <b>{counts.covered}</b> covered
          </span>
        </div>
        <div className="sw-count">
          <span className="sw-count-dot partial" />
          <span>
            <b>{counts.partial}</b> partial
          </span>
        </div>
        <div className="sw-count">
          <span className="sw-count-dot not-covered" />
          <span>
            <b>{counts.notCovered}</b> not covered
          </span>
        </div>
      </div>
      {result?.overall_assessment && (
        <div className="sw-assessment">{result.overall_assessment}</div>
      )}
    </div>
  );
};

SummaryCard.propTypes = {
  name: PropTypes.string.isRequired,
  result: PropTypes.object,
};

const MatrixRows = ({ specifications, subcontractors, results, gridStyle }) =>
  specifications.map((spec, index) => (
    <div className="sw-matrix-row" style={gridStyle} key={index}>
      <div>
        <div className="sw-spec-name">{spec.item_name}</div>
        {spec.item_description && (
          <div className="sw-spec-desc">{spec.item_description}</div>
        )}
      </div>
      {subcontractors.map((name) => {
        const result = results[name];
        if (result?.error) {
          return (
            <div key={name} className="sw-coverage-cell">
              <span className="sw-chip tone-danger">
                <AlertCircle size={13} />
                Analysis failed
              </span>
            </div>
          );
        }
        const itemCoverage = result?.items_covered?.find(
          (item) => item.item === spec.item_name
        );
        return (
          <div key={name} className="sw-coverage-cell">
            <CoverageChip coverage={itemCoverage?.coverage} />
            {itemCoverage?.explanation && (
              <span className="sw-coverage-note">
                {itemCoverage.explanation}
              </span>
            )}
          </div>
        );
      })}
    </div>
  ));

const ScopeAnalysisSection = ({ scopeAnalysisData, isLoading }) => {
  const failuresRef = useRef(null);

  if (isLoading) {
    return <ScopeAnalysisLoading />;
  }

  if (!scopeAnalysisData) {
    return <NoSpecificationsEmpty />;
  }

  const {
    extracted_specifications,
    subcontractor_results = {},
    scope_document,
    status,
  } = scopeAnalysisData;

  const clearSpecs = extracted_specifications?.clear_specifications || [];
  const unclearSpecs =
    extracted_specifications?.open_unclear_specifications || [];
  const hasSpecifications = clearSpecs.length > 0 || unclearSpecs.length > 0;

  if (!hasSpecifications) {
    return <NoSpecificationsEmpty />;
  }

  // A global analysis error is stored directly in subcontractor_results
  const isGlobalError =
    subcontractor_results.error && subcontractor_results.timestamp;
  const actualSubcontractorResults = isGlobalError ? {} : subcontractor_results;
  const hasResults = Object.keys(actualSubcontractorResults).length > 0;

  if ((isGlobalError || status === 'FAILURE') && !hasResults) {
    return (
      <div className="sw-root scope-analysis-section">
        <div className="scope-global-error-banner">
          <div className="banner-content">
            <div className="error-header">
              <strong>Analysis Failed</strong>
            </div>
            <div className="error-message">
              {isGlobalError
                ? subcontractor_results.error
                : 'The scope analysis failed to complete.'}
            </div>
            {isGlobalError && subcontractor_results.timestamp && (
              <div className="error-timestamp">
                Failed at:{' '}
                {new Date(subcontractor_results.timestamp).toLocaleString()}
              </div>
            )}
            <div className="error-note">
              Specifications were successfully extracted from the scope
              document, but quote analysis failed.
            </div>
          </div>
        </div>

        <div className="sw-card">
          <div className="sw-card-title">
            Extracted specifications ({clearSpecs.length + unclearSpecs.length})
          </div>
          {clearSpecs.length > 0 && (
            <>
              <div className="sw-matrix-group">
                Clear specifications ({clearSpecs.length})
              </div>
              <ul className="sw-bullets">
                {clearSpecs.map((spec, index) => (
                  <li key={index}>
                    <b>{spec.item_name}</b>
                    {spec.item_description}
                  </li>
                ))}
              </ul>
            </>
          )}
          {unclearSpecs.length > 0 && (
            <>
              <div className="sw-matrix-group">
                General specifications ({unclearSpecs.length})
              </div>
              <ul className="sw-bullets">
                {unclearSpecs.map((spec, index) => (
                  <li key={index}>
                    <b>{spec.item_name}</b>
                    {spec.item_description}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </div>
    );
  }

  if (!hasResults) {
    return <NoSubcontractorResults />;
  }

  const subcontractors = Object.keys(actualSubcontractorResults);
  const hasFailures = Object.values(actualSubcontractorResults).some(
    (result) => result.error
  );
  const totalSpecs = clearSpecs.length + unclearSpecs.length;

  const gridStyle = {
    gridTemplateColumns: `minmax(200px, 1.2fr) repeat(${subcontractors.length}, minmax(160px, 1fr))`,
  };

  const issues = subcontractors
    .map((name) => ({
      name,
      issues: actualSubcontractorResults[name]?.potential_issues || [],
    }))
    .filter((entry) => entry.issues.length > 0);

  const additionalItems = subcontractors
    .map((name) => ({
      name,
      items: actualSubcontractorResults[name]?.additional_items || [],
    }))
    .filter((entry) => entry.items.length > 0);

  return (
    <div className="sw-root scope-analysis-section">
      {hasFailures && (
        <ScopeErrorBanner
          subcontractorResults={actualSubcontractorResults}
          onViewDetails={() =>
            failuresRef.current?.scrollIntoView({ behavior: 'smooth' })
          }
        />
      )}

      {/* Intro */}
      <div className="sw-card sw-intro">
        <FileText size={22} className="sw-intro-icon" />
        <div>
          <div className="sw-intro-title">Scope of Works</div>
          <div className="sw-intro-text">
            {totalSpecs} scope items extracted
            {scope_document?.file_name
              ? ` from ${scope_document.file_name}`
              : ''}{' '}
            and checked against each subcontractor&apos;s quote.
          </div>
        </div>
      </div>

      {/* Per-subcontractor summary */}
      <div className="sw-grid-2">
        {subcontractors.map((name) => (
          <SummaryCard
            key={name}
            name={name}
            result={actualSubcontractorResults[name]}
          />
        ))}
      </div>

      {/* Coverage matrix */}
      <div className="sw-card">
        <div className="sw-card-title">
          Coverage against the {totalSpecs} scope requirement
          {totalSpecs !== 1 ? 's' : ''}
        </div>
        <div className="sw-matrix">
          <div className="sw-matrix-head" style={gridStyle}>
            <span>Scope item</span>
            {subcontractors.map((name) => (
              <span key={name}>{name}</span>
            ))}
          </div>
          {clearSpecs.length > 0 && (
            <>
              <div className="sw-matrix-group">Clear specifications</div>
              <MatrixRows
                specifications={clearSpecs}
                subcontractors={subcontractors}
                results={actualSubcontractorResults}
                gridStyle={gridStyle}
              />
            </>
          )}
          {unclearSpecs.length > 0 && (
            <>
              <div className="sw-matrix-group">General specifications</div>
              <MatrixRows
                specifications={unclearSpecs}
                subcontractors={subcontractors}
                results={actualSubcontractorResults}
                gridStyle={gridStyle}
              />
            </>
          )}
        </div>
      </div>

      {/* Additional items */}
      {additionalItems.length > 0 && (
        <div className="sw-grid-2">
          {additionalItems.map(({ name, items }) => (
            <div className="sw-card" key={name}>
              <div className="sw-count-head">
                <span className="sw-chip sw-sub-chip tone-neutral">{name}</span>
                <span className="spacer" />
                <span className="sw-chip tone-info">
                  {items.length} additional item{items.length !== 1 ? 's' : ''}
                </span>
              </div>
              <ul className="sw-bullets">
                {items.map((item, index) => (
                  <li key={index}>
                    <b>{item.item}</b>
                    {item.explanation}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      {/* Potential issues */}
      <div>
        <div className="sw-card-title" style={{ marginBottom: 12 }}>
          Potential issues
        </div>
        {issues.length > 0 ? (
          <div className="sw-grid-2">
            {issues.flatMap(({ name, issues: subIssues }) =>
              subIssues.map((issue, index) => (
                <div className="sw-card sw-issue-card" key={`${name}-${index}`}>
                  <Flag size={18} className="sw-issue-icon" />
                  <div>
                    <span className="sw-chip tone-neutral">{name}</span>
                    <div className="sw-issue-title">{issue.issue}</div>
                    <div className="sw-issue-text">{issue.explanation}</div>
                  </div>
                </div>
              ))
            )}
          </div>
        ) : (
          <div className="sw-card sw-no-issues">
            No potential issues identified — all subcontractors appear to have
            good alignment with the scope requirements.
          </div>
        )}
      </div>

      {/* Failed analyses */}
      {hasFailures && (
        <div ref={failuresRef}>
          <ScopeFailedAnalysesTab
            subcontractorResults={actualSubcontractorResults}
          />
        </div>
      )}
    </div>
  );
};

ScopeAnalysisSection.propTypes = {
  scopeAnalysisData: PropTypes.shape({
    extracted_specifications: PropTypes.shape({
      clear_specifications: PropTypes.array,
      open_unclear_specifications: PropTypes.array,
    }),
    subcontractor_results: PropTypes.object,
    scope_document: PropTypes.object,
    sow_text: PropTypes.string,
    status: PropTypes.string,
  }),
  isLoading: PropTypes.bool,
};

export default ScopeAnalysisSection;
